import { db } from './firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import store from './store';

let cachedCustomerList = undefined;
let cachedCustomerMap = undefined;

const customer = {
  init: async function () {
    const localUpdatedTime = localStorage?.getItem('updatedTime.customers');
    const remoteUpdatedTime = store.state.settings.updatedTime.customers.toDate();

    if (localUpdatedTime && new Date(Number(localUpdatedTime)) >= remoteUpdatedTime) {
      let maps = {};
      cachedCustomerList = JSON.parse(localStorage.getItem('customers'));
      cachedCustomerList.forEach((item) => {
        maps[item.id] = item;
      });
      cachedCustomerMap = maps;
    } else {
      await customer._load();
    }
  },

  save: async function (customer) {
    const item = {
      name: customer.name.trim() || '',
      englishName: customer.englishName.trim() || '',
      code: customer.code.trim() || '',
      phone: customer.phone.trim() || '',
      boss: customer.boss.trim() || '',
      mobile: customer.mobile.trim() || '',
      disabled: customer.disabled || false,
      manager: customer.manager.trim() || '',
      managerMobile: customer.managerMobile.trim() || '',
      registerTime: customer.registerTime ? new Date(customer.registerTime) : serverTimestamp(),
      region: customer.region.trim() || '',
      memo: customer.memo.trim() || '',
    };
    let modify = '';
    if (!customer.id || customer.id == '') {
      await addDoc(collection(db, 'customers'), item);
      modify = 'add';
    } else {
      await updateDoc(doc(db, 'customers', customer.id), item);
      modify = 'update';
    }

    await store.dispatch('log', {
      module: 'customer',
      id: store.state.user.id,
      text:
        modify +
        ' ' +
        [
          item.code,
          item.name,
          item.englishName,
          item.boss,
          item.phone,
          item.mobile,
          item.manager,
          item.managerMobile,
          item.registerTime,
          item.disabled,
        ].join(' | '),
    });
  },

  list: function () {
    return cachedCustomerList;
  },

  _load: async function () {
    let items = [];
    let maps = {};
    let snapshot = await getDocs(collection(db, 'customers'));
    snapshot.forEach((doc) => {
      let item = doc.data();
      let customer = {};
      customer.id = doc.id;
      customer.name = item.name;
      customer.englishName = item.englishName;
      customer.code = item.code;
      customer.phone = item.phone;
      customer.boss = item.boss;
      customer.mobile = item.mobile;
      customer.disabled = item.disabled || false;
      customer.registerTime = item.registerTime ? item.registerTime.toDate() : '';
      customer.manager = item.manager || '';
      customer.managerMobile = item.managerMobile || '';
      customer.region = item.region || '';
      customer.memo = item.memo || '';
      customer.label = `${item.name}${item.region ? ' | ' + item.region : ''}${item.memo ? ' | ' + item.memo : ''}`;
      items.push(customer);
      maps[doc.id] = customer;
    });
    cachedCustomerMap = maps;
    cachedCustomerList = items.sort(function (a, b) {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });

    const remoteUpdatedTime = store.state.settings.updatedTime.customers.toDate();
    localStorage.setItem('customers', JSON.stringify(cachedCustomerList));
    localStorage.setItem('updatedTime.customers', remoteUpdatedTime.getTime());
  },

  lookupCustomer: function (id) {
    return cachedCustomerMap[id] || {};
  },

  subscribe: function (callback) {
    let first = true;
    return onSnapshot(doc(db, 'admin', 'settings'), (docSnap) => {
      // Only server change.
      if (!docSnap.metadata.hasPendingWrites) {
        // The first snapshot change is always called.
        if (first) first = false;
        else callback(docSnap.data());
      }
    });
  },

  unsubscribe: function (listener) {
    typeof listener === 'function' && listener();
  },

  getCellText: (cell) => {
    if (!cell) return cell;
    if (!cell.richText)
      return cell
        .toString()
        .replace(/(\r\n|\n|\r)/gm, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return cell.richText
      .map((v) => v.text)
      .join('')
      .replace(/(\r\n|\n|\r)/gm, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  },

  loadExcelFile: async (filename) => {
    let customerList = cachedCustomerList;
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.onload = async () => {
        let customers = [];
        const ExcelJS = await import(/* webpackChunkName: "exceljs" */ 'exceljs');
        const workbook = new ExcelJS.Workbook();
        const wb = await workbook.xlsx.load(reader.result);
        const sheet = wb.getWorksheet(1);
        sheet.eachRow((row, index) => {
          if (index <= 2) return;
          const doc = {
            code: customer.getCellText(row.values[1]) || '',
            name: customer.getCellText(row.values[2]) || '',
            boss: customer.getCellText(row.values[3]) || '',
            phone: customer.getCellText(row.values[4]) || '',
            mobile: customer.getCellText(row.values[5]) || '',
            manager: customer.getCellText(row.values[9]) || '',
            managerMobile: customer.getCellText(row.values[10]) || '',
            registerTime: customer.getCellText(row.values[11]) || '',
            englishName: customer.getCellText(row.values[12]) || '',
          };

          let found = customerList.find((item) => item.code == doc.code);
          if (!found) {
            doc.change = { add: true };
            customers.push(doc);
          } else {
            if (
              found.name != doc.name ||
              found.boss != doc.boss ||
              found.phone != doc.phone ||
              found.mobile != doc.mobile ||
              found.manager != doc.manager ||
              found.managerMobile != doc.managerMobile ||
              found.englishName != doc.englishName
            ) {
              doc.change = { modify: true };
              doc.id = found.id;
              customers.push(doc);
            }
          }
        });
        resolve(customers);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(filename);
    });
  },
};

export default customer;
